import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { useUserStore } from "@/lib/fetch-users"
import { useWaitingRoomFiltersContext } from "@/modules/waiting-room/contexts/waiting-room-filters-context"
import {
  AVAILABLE_TO_ME_WORKING_ROLES,
  AvailableToMeWorkingRole,
  getAvailableToMeFilters,
  getDoctorLanguagesSkill,
} from "@/modules/waiting-room/helpers/waiting-room-helper"

const WORKING_ROLE_TRANSLATION_KEYS: Record<AvailableToMeWorkingRole, string> = {
  MedicalSupervisor: "scheduling.waitingRoom.filters.availableToMeFilter.workingAsMedicalSupervisor",
  Doctor: "scheduling.waitingRoom.filters.availableToMeFilter.workingAsDoctor",
}

const AvailableToMeFilter = () => {
  const { t } = useTranslation()
  const setFilters = useWaitingRoomFiltersContext((state) => state.setFilters)
  const { userName, role, isOneOf } = useUser()

  const loginUser = useUserStore((state) => state.loginUser)

  const doctorLanguages = useMemo(() => {
    return loginUser ? getDoctorLanguagesSkill(loginUser) : null
  }, [loginUser])

  const isMedicalSupervisor = isOneOf(["MedicalSupervisor"])

  const applyWorkingRolePreset = (workingRole: AvailableToMeWorkingRole) => {
    if (loginUser) {
      setFilters(getAvailableToMeFilters(workingRole, userName, doctorLanguages))
    }
  }

  const handleAvailableToMeFilter = () => {
    if (loginUser) {
      const filters = getAvailableToMeFilters(role, userName, doctorLanguages)
      setFilters(filters)
    }
  }

  if (!isMedicalSupervisor) {
    return (
      <Button
        variant="tertiary"
        onClick={handleAvailableToMeFilter}
        type="button"
        data-testid="available-to-me-btn"
        data-faro-user-action-name="filter_available_to_me"
        className="h-8">
        {t("scheduling.waitingRoom.filters.availableToMe")}
      </Button>
    )
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="tertiary"
          type="button"
          data-testid="available-to-me-btn"
          data-faro-user-action-name="filter_available_to_me"
          className="h-8 gap-0.5">
          {t("scheduling.waitingRoom.filters.availableToMe")}
          <CaretDown size={20} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="start" dataTestId="available-to-me-menu" className="rounded-lg p-2">
        {AVAILABLE_TO_ME_WORKING_ROLES.map((workingRole) => (
          <DropdownMenuItem
            key={workingRole}
            className="focus:bg-surface-hovered rounded-md py-2"
            data-testid={`available-to-me-${workingRole}`}
            onClick={() => applyWorkingRolePreset(workingRole)}>
            {t(WORKING_ROLE_TRANSLATION_KEYS[workingRole])}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

AvailableToMeFilter.displayName = "AvailableToMeFilter"

export { AvailableToMeFilter }
